import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { focusFirstFieldOnTab, tabBetweenFields } from "src/utils/countFields"
import { units as unitArt } from "src/assets/images"

import "./UnitPicker.css"

/* Whether a focused element wants the space bar for itself. The picker's own sliders and count
   fields do not: a slider ignores it and a count is digits only — so Space can carry the sheet
   forward straight after a drag or a typed count (owner, 2026-09-29). */
export const takesText = (element) => {
  if (!element) return false
  if (element.tagName === "TEXTAREA" || element.isContentEditable) return true
  if (element.tagName !== "INPUT") return false
  if (element.type === "range" || element.classList?.contains("march-unit-number")) return false
  return true
}

// Tab walks the count fields, past the unit buttons and sliders between them (`utils/countFields`).
const tabBetweenCounts = tabBetweenFields(".march-unit-number")

/* The force picker the march order was built around, and the tunnel sheet's too (owner,
   2026-09-29: "the same UI as the troop-movement picker"). One card per unit type — the art and
   the name are the "all of them" button (R29), a slider on a row of its own, a typed count — under
   a section head that carries the 100% / 50% / 0% shares when `onFill` is given.

   `units` is `[{type, available, max?}]`: `available` is what the card says stands there, `max` the
   most this sheet may take of it (the slider's end and the "all" button's number), `available` by
   default. `onChange(type, number)` gets what was pressed, dragged or typed; clamping is the
   sheet's business, since only the sheet knows its limit. The first Tab lands on the first count
   and Tab walks the counts, as in the city's production queue. */
const UnitPicker = ({ units, value, onChange, disabled = false, label, onFill, className = "", emptyText }) => {
  const { t } = useTranslation()

  const pickerRef = useRef(null)
  useEffect(() => focusFirstFieldOnTab(pickerRef.current, ".march-unit-number"), [])

  return (
    <section className={`unit-picker ${className}`.trim()} ref={pickerRef}>
      <div className="march-section-head">
        <span className="section-label">{label}</span>
        <span className="march-rule" />
        {/* A share of the army, as the percentage it is (owner, 2026-09-29: "bigger and more
            visible"). The words stay as the accessible name. */}
        {onFill && (
          <span className="march-quick" role="group">
            <button type="button" data-action="all-in" aria-label={t("attack.allIn")} title={t("attack.allIn")} disabled={disabled} onClick={() => onFill(1)}>
              100%
            </button>
            <button type="button" data-action="half" aria-label={t("attack.half")} title={t("attack.half")} disabled={disabled} onClick={() => onFill(0.5)}>
              50%
            </button>
            <button type="button" data-action="none" aria-label={t("attack.none")} title={t("attack.none")} disabled={disabled} onClick={() => onFill(0)}>
              0%
            </button>
          </span>
        )}
      </div>

      {units.length === 0 && emptyText && <p className="march-empty">{emptyText}</p>}

      {units.map(({ type, available, max = available }) => {
        const chosenNumber = Number(value?.[type]) || 0
        const fill = max > 0 ? Math.min(100, (chosenNumber / max) * 100) : 0
        return (
          <label className={`march-unit${chosenNumber > 0 ? " active" : ""}`} key={type}>
            {/* The unit itself is the "all of them" button (owner's R29): pressing the art or
                the name takes every one it may — the count next to it says how many stand here. */}
            <button
              type="button"
              className="march-unit-all"
              data-unit={type}
              title={t("attack.selectAll", { count: max })}
              disabled={disabled}
              onClick={() => onChange(type, chosenNumber === max ? 0 : max)}
            >
              <img src={unitArt[type]} alt="" className="march-unit-art" />
              <span className="march-unit-name">
                {t(`city.units.${type}`, { defaultValue: type })}
                <span className="march-unit-here">{t("attack.availableHere", { count: available })}</span>
              </span>
            </button>
            <input
              type="range"
              min="0"
              max={max}
              value={chosenNumber}
              disabled={disabled}
              onChange={(event) => onChange(type, event.target.value)}
              style={{ "--fill": `${fill}%` }}
            />
            {/* A text field with a numeric keypad rather than type="number" (owner's R29):
                Chrome drew the number input with no digits at all in this layout — the
                slider moved, the field stayed blank — and a text field draws what it holds. */}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="march-unit-number"
              value={chosenNumber}
              disabled={disabled}
              onKeyDown={tabBetweenCounts}
              onChange={(event) => onChange(type, event.target.value)}
            />
          </label>
        )
      })}
    </section>
  )
}

export default UnitPicker
